import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Box, Container, useMediaQuery, type Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
	Bar,
	BarChart,
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
	type XAxisTickContentProps,
} from 'recharts';
import { type ChartEntry, EntityType } from '../types';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectAllRepositoryEntries, selectAllRepositories, selectAllPropertyDefinitions } from '../containers/contentSlice';
import { populateRepositoryEntriesForTable } from '../common/util';
import { filterOfflineRowsByArchivedAndDataFilter } from '../common/offlineDataFilter';
import { selectAllEntryProperties } from '../containers/contentSlice';
import {
	useGetRepositoryByIdQuery,
	useGetRepositoryEntriesByFilterQuery,
} from '../services/repositoryEntries';
import {
	buildCanvasChartSeries,
	CHART_COUNT_METRIC_VALUE,
	isChartDateLikePropertyType,
	resolveChartTargetReference,
	type ChartPresentationVariant,
	type ChartTimeScale,
} from '../chart/canvasChartData';
import ChartContributionHeatmap from '../chart/ChartContributionHeatmap';
import ChartTargetReferenceLines from '../chart/ChartTargetReferenceLines';
import {
	parseResolvedChartDataFilterBoundsForSlug,
	resolveChartWhereForApi,
} from '../chart/resolveChartWhereForApi';

dayjs.extend(relativeTime);

function parseBucketTickToDayjs(raw: string): dayjs.Dayjs {
	const trimmed = raw.trim();
	if (/^\d{4}-\d{2}$/.test(trimmed)) {
		return dayjs(`${trimmed}-01`);
	}
	return dayjs(trimmed);
}

function formatBucketMainTickLabel(value: string, timeScale: ChartTimeScale): string {
	const d = parseBucketTickToDayjs(value);
	if (!d.isValid()) {
		return value;
	}
	if (timeScale === 'M') {
		return d.format('MMM YYYY');
	}
	return d.format('MMM D');
}

const ChartDetailXAxisTick = (props: XAxisTickContentProps & { timeScale: ChartTimeScale }) => {
	const theme = useTheme();
	const { x, y, payload, timeScale } = props;
	const xN = Number(x);
	const yN = Number(y);
	const value = String(payload?.value ?? '');
	const d = parseBucketTickToDayjs(value);
	const main = formatBucketMainTickLabel(value, timeScale);
	const relativePhrase = d.isValid() ? d.fromNow() : '';
	return (
		<g transform={`translate(${xN},${yN})`}>
			<text x={0} y={0} dy={12} textAnchor="middle" fill={theme.palette.text.primary} fontSize={12}>
				{main}
			</text>
			{relativePhrase ? (
				<text x={0} y={0} dy={26} textAnchor="middle" fill={theme.palette.text.secondary} fontSize={10}>
					{relativePhrase}
				</text>
			) : null}
		</g>
	);
};

function normalizeTimeScale(raw: string | null | undefined): ChartTimeScale | null {
	const t = (raw ?? '').trim();
	if (t === 'D' || t === 'W' || t === 'M') {
		return t;
	}
	return null;
}

export interface ChartDetailProps {
	chart: ChartEntry;
}

const ChartDetail: React.FC<ChartDetailProps> = ({ chart }) => {
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);
	const allRepositories = useAppSelector(selectAllRepositories);
	const repositoryId = chart.repositoryId ?? '';
	const where = chart.where ?? null;
	const includeArchived = chart.includeArchived !== false;
	const metricKey = (chart.metric ?? '').trim();
	const xAxisKey = (chart.xAxis ?? '').trim();
	const vr = (chart.variant ?? '').trim();
	const presentationVariant: ChartPresentationVariant =
		vr === 'bar' || vr === 'heatmap' ? vr : 'line';
	const timeScaleFromChart = normalizeTimeScale(chart.xAxisTimeScale ?? null);

	const { data: repositoryById } = useGetRepositoryByIdQuery(repositoryId, {
		skip: !isAuthenticated || !repositoryId,
	});
	const xAxisPropertyType = useMemo(() => {
		if (!xAxisKey) {
			return undefined;
		}
		if (isAuthenticated) {
			return repositoryById?.propertyDefinitions?.find((def) => def.slug === xAxisKey)?.type as EntityType | undefined;
		}
		return allPropertyDefinitions.find((def) => def.parentId === repositoryId && def.slug === xAxisKey)?.type as
			| EntityType
			| undefined;
	}, [allPropertyDefinitions, isAuthenticated, repositoryById?.propertyDefinitions, repositoryId, xAxisKey]);
	const dateLikeAxis = isChartDateLikePropertyType(xAxisPropertyType);
	const heatmapMode = presentationVariant === 'heatmap' && dateLikeAxis;
	const useTimeBucketsForSeries = heatmapMode || Boolean(timeScaleFromChart && dateLikeAxis);
	const timeScaleForSeries: ChartTimeScale | null = heatmapMode ? 'D' : timeScaleFromChart;
	const resolvedDataFilter = useMemo(() => resolveChartWhereForApi(where), [where]);
	const timeBucketRangeFromFilter = useMemo(() => {
		if (!useTimeBucketsForSeries || !resolvedDataFilter || !xAxisKey) {
			return null;
		}
		return parseResolvedChartDataFilterBoundsForSlug(resolvedDataFilter, xAxisKey);
	}, [useTimeBucketsForSeries, resolvedDataFilter, xAxisKey]);

	const { data, isFetching } = useGetRepositoryEntriesByFilterQuery({
		repositoryId,
		dataFilter: resolvedDataFilter,
		includeArchived,
		sortBy: useTimeBucketsForSeries ? undefined : (xAxisKey || undefined),
		sortDir: 'asc',
	}, {
		skip: !isAuthenticated || !repositoryId,
	});

	const offlineRepositoryEntries = useMemo(() => {
		if (isAuthenticated || !repositoryId) {
			return undefined;
		}
		const repository = allRepositories.find((r) => r.id === repositoryId);
		if (!repository) {
			return [];
		}
		const populated = populateRepositoryEntriesForTable(repository, allRepositoryEntries, allPropertyDefinitions, allEntryProperties);
		let filtered = filterOfflineRowsByArchivedAndDataFilter(populated, includeArchived, resolvedDataFilter);
		if (!useTimeBucketsForSeries && xAxisKey) {
			filtered = [...filtered].sort((a, b) => {
				const av = a.data?.[xAxisKey];
				const bv = b.data?.[xAxisKey];
				const an = Number(av);
				const bn = Number(bv);
				if (Number.isFinite(an) && Number.isFinite(bn)) {
					return an - bn;
				}
				return String(av ?? '').localeCompare(String(bv ?? ''));
			});
		}
		return filtered;
	}, [
		allRepositoryEntries,
		allPropertyDefinitions,
		allEntryProperties,
		allRepositories,
		includeArchived,
		isAuthenticated,
		repositoryId,
		resolvedDataFilter,
		useTimeBucketsForSeries,
		xAxisKey,
	]);

	const rows = isAuthenticated ? (data?.rows ?? []) : (offlineRepositoryEntries ?? []);
	const chartData = useMemo(
		() => buildCanvasChartSeries({
			rows,
			metricKey,
			xAxisKey,
			timeScale: timeScaleForSeries,
			useTimeBuckets: useTimeBucketsForSeries,
			timeBucketRangeFromFilter,
			missingBucketMetric: presentationVariant === 'line' ? 'null' : 'zero',
		}),
		[
			rows,
			metricKey,
			xAxisKey,
			timeScaleForSeries,
			useTimeBucketsForSeries,
			timeBucketRangeFromFilter,
			presentationVariant,
		],
	);
	const repositoryTitle = useMemo(() => {
		if (isAuthenticated) {
			return repositoryById?.repository?.title?.trim() ?? '';
		}
		return allRepositories.find((r) => r.id === repositoryId)?.title?.trim() ?? '';
	}, [allRepositories, isAuthenticated, repositoryById?.repository?.title, repositoryId]);
	const metricLabel = useMemo(() => {
		if (metricKey === CHART_COUNT_METRIC_VALUE) {
			return 'Number of entries';
		}
		const fromApi = repositoryById?.propertyDefinitions?.find((def) => def.slug === metricKey)?.label;
		const fromLocal = allPropertyDefinitions.find((def) => def.parentId === repositoryId && def.slug === metricKey)
			?.label;
		const trimmed = String((isAuthenticated ? fromApi : fromLocal) ?? '').trim();
		return trimmed || metricKey || 'Metric';
	}, [allPropertyDefinitions, isAuthenticated, metricKey, repositoryById?.propertyDefinitions, repositoryId]);
	const isLoadingChartData = Boolean(isAuthenticated && isFetching && repositoryId);
	useLayoutEffect(() => {
		if (!heatmapMode || isLoadingChartData) return;
		window.scrollTo({ left: document.documentElement.scrollWidth });
	}, [heatmapMode, isLoadingChartData]);
	const isSmScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const rootRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		const el = rootRef.current;
		if (!el || !heatmapMode || isSmScreen) return;
		const handleWheel = (e: WheelEvent) => {
			if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
			const root = document.documentElement;
			if (root.scrollWidth <= root.clientWidth) return;
			e.preventDefault();
			window.scrollBy({ left: e.deltaY });
		};
		el.addEventListener('wheel', handleWheel, { passive: false });
		return () => el.removeEventListener('wheel', handleWheel);
	}, [heatmapMode, isSmScreen]);
	const chartBottomMargin = useTimeBucketsForSeries && timeScaleForSeries ? 48 : 12;
	const xAxisTick = useMemo(() => {
		if (!useTimeBucketsForSeries || !timeScaleForSeries) {
			return undefined;
		}
		// eslint-disable-next-line react/display-name
		return (tickProps: XAxisTickContentProps) => (
			<ChartDetailXAxisTick {...tickProps} timeScale={timeScaleForSeries} />
		);
	}, [timeScaleForSeries, useTimeBucketsForSeries]);
	const showBar = presentationVariant === 'bar' && !heatmapMode;
	const theme = useTheme();
	const chartTarget = useMemo(
		() =>
			resolveChartTargetReference({
				targetA: chart.targetA,
				targetB: chart.targetB,
				chartData,
			}),
		[chart.targetA, chart.targetB, chartData],
	);
	const targetStroke = theme.palette.warning.main;

	return (
		<Box
			ref={rootRef}
			sx={{
				flex: 1,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				px: 3,
				py: 2,
				// Grows to an overflowing heatmap's width, so the side padding stays on both ends while scrolling the page.
				...(heatmapMode && { width: 'max-content', minWidth: '100%' }),
			}}
		>
			<Container maxWidth={false} disableGutters>
				{heatmapMode ? (
					<Box sx={{ py: 2 }}>
						{chartData.length > 0 ? (
							<ChartContributionHeatmap series={chartData} range={timeBucketRangeFromFilter} cellGap={0} height="50vh" appearance="grid" showWeekLabels />
						) : (
							<Box sx={{ color: 'text.secondary', fontSize: 14 }}>No data in this range.</Box>
						)}
					</Box>
				) : (
					<ResponsiveContainer width="100%" aspect={1.618} maxHeight={480}>
						{showBar ? (
							<BarChart data={chartData} margin={{ top: 12, right: 16, bottom: chartBottomMargin, left: 8 }}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis
									dataKey="xValue"
									tick={xAxisTick}
									height={useTimeBucketsForSeries && timeScaleForSeries ? 56 : undefined}
								/>
								<YAxis width={56} />
								<ChartTargetReferenceLines reference={chartTarget} stroke={targetStroke} />
								<RechartsTooltip formatter={(value) => [value ?? '', metricLabel]} />
								<Bar
									dataKey="metricValue"
									fill="#8884d8"
									maxBarSize={56}
									radius={[4, 4, 0, 0]}
									isAnimationActive={false}
								/>
							</BarChart>
						) : (
							<LineChart data={chartData} margin={{ top: 12, right: 16, bottom: chartBottomMargin, left: 8 }}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis
									dataKey="xValue"
									tick={xAxisTick}
									height={useTimeBucketsForSeries && timeScaleForSeries ? 56 : undefined}
								/>
								<YAxis width={56} />
								<ChartTargetReferenceLines reference={chartTarget} stroke={targetStroke} />
								<RechartsTooltip formatter={(value) => [value ?? '', metricLabel]} />
								<Line
									type="monotone"
									dataKey="metricValue"
									stroke="#8884d8"
									strokeWidth={2}
									connectNulls={true}
									dot={{ r: 2 }}
									activeDot={{ r: 5 }}
								/>
							</LineChart>
						)}
					</ResponsiveContainer>
				)}
				<Box
					sx={{
						mt: 1,
						color: 'text.secondary',
						fontSize: 13,
						textAlign: 'center',
						// Viewport-wide and sticky, so it stays centered on screen while an overflowing heatmap scrolls the page.
						position: 'sticky',
						left: (theme) => theme.spacing(3),
						width: (theme) => `calc(100vw - ${theme.spacing(6)})`,
					}}
				>
					{isLoadingChartData
						? 'Loading chart data...'
						: `${rows.length} records from ${repositoryTitle || 'repository'}`}
				</Box>
			</Container>
		</Box>
	);
};

export default ChartDetail;
